import { Injectable } from '@angular/core';

export interface CacheEntry {
  payload: any;
  response: any;
  timestamp: number;
  ttl: number; // Time to live in milliseconds
}

/** API response cache using sessionStorage (e.g. Insights Dashboard overview) – cleared when tab closes. */
@Injectable({
  providedIn: 'root'
})
export class ApiCacheService {
  private readonly CACHE_PREFIX = 'api_cache_';
  private readonly DEFAULT_TTL = 5 * 60 * 1000; // 5 minutes default TTL

  /**
   * Generate a cache key from the payload
   */
  private generateCacheKey(endpoint: string, payload: any): string {
    const payloadString = JSON.stringify(payload);
    const hash = this.simpleHash(payloadString);
    return `${this.CACHE_PREFIX}${endpoint}_${hash}`;
  }

  /**
   * Simple hash function for payload
   */
  private simpleHash(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Check if cache entry exists and is valid
   */
  hasValidCache(endpoint: string, payload: any): boolean {
    const cacheKey = this.generateCacheKey(endpoint, payload);
    const cached = sessionStorage.getItem(cacheKey);

    if (!cached) {
      return false;
    }

    try {
      const entry: CacheEntry = JSON.parse(cached);
      const now = Date.now();

      // Check if cache has expired
      if (now - entry.timestamp > entry.ttl) {
        this.removeCache(endpoint, payload);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Error parsing cache entry:', error);
      this.removeCache(endpoint, payload);
      return false;
    }
  }

  /**
   * Get cached response
   */
  getCache(endpoint: string, payload: any): any | null {
    const cacheKey = this.generateCacheKey(endpoint, payload);
    const cached = sessionStorage.getItem(cacheKey);

    if (!cached) {
      return null;
    }

    try {
      const entry: CacheEntry = JSON.parse(cached);
      const now = Date.now();

      // Check if cache has expired
      if (now - entry.timestamp > entry.ttl) {
        this.removeCache(endpoint, payload);
        return null;
      }

      return entry.response;
    } catch (error) {
      console.error('Error parsing cache entry:', error);
      this.removeCache(endpoint, payload);
      return null;
    }
  }

  /**
   * Store response in cache
   */
  setCache(endpoint: string, payload: any, response: any, ttl?: number): void {
    const cacheKey = this.generateCacheKey(endpoint, payload);
    const entry: CacheEntry = {
      payload: payload,
      response: response,
      timestamp: Date.now(),
      ttl: ttl || this.DEFAULT_TTL
    };

    try {
      sessionStorage.setItem(cacheKey, JSON.stringify(entry));
    } catch (error) {
      console.error('Error storing cache entry:', error);
      // If sessionStorage is full, clear old entries
      this.clearOldEntries();
      try {
        sessionStorage.setItem(cacheKey, JSON.stringify(entry));
      } catch (retryError) {
        console.error('Failed to store cache even after cleanup:', retryError);
      }
    }
  }

  /**
   * Remove specific cache entry
   */
  removeCache(endpoint: string, payload: any): void {
    const cacheKey = this.generateCacheKey(endpoint, payload);
    sessionStorage.removeItem(cacheKey);
  }

  /**
   * Clear all cache entries for a specific endpoint
   */
  clearEndpointCache(endpoint: string): void {
    const keys = Object.keys(sessionStorage);
    const prefix = `${this.CACHE_PREFIX}${endpoint}_`;

    keys.forEach(key => {
      if (key.startsWith(prefix)) {
        sessionStorage.removeItem(key);
      }
    });
  }

  /**
   * Clear all API cache entries
   */
  clearAllCache(): void {
    const keys = Object.keys(sessionStorage);

    keys.forEach(key => {
      if (key.startsWith(this.CACHE_PREFIX)) {
        sessionStorage.removeItem(key);
      }
    });
  }

  /**
   * Clear old cache entries to free up space
   */
  private clearOldEntries(): void {
    const keys = Object.keys(sessionStorage);
    const now = Date.now();

    keys.forEach(key => {
      if (key.startsWith(this.CACHE_PREFIX)) {
        try {
          const cached = sessionStorage.getItem(key);
          if (cached) {
            const entry: CacheEntry = JSON.parse(cached);
            if (now - entry.timestamp > entry.ttl) {
              sessionStorage.removeItem(key);
            }
          }
        } catch (error) {
          // Remove corrupted entries
          sessionStorage.removeItem(key);
        }
      }
    });
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): { totalEntries: number; totalSize: number; entries: Array<{ key: string; size: number; age: number }> } {
    const keys = Object.keys(sessionStorage);
    const cacheKeys = keys.filter(key => key.startsWith(this.CACHE_PREFIX));
    const now = Date.now();

    const entries = cacheKeys.map(key => {
      const cached = sessionStorage.getItem(key);
      const size = cached ? cached.length : 0;
      let age = 0;

      try {
        if (cached) {
          const entry: CacheEntry = JSON.parse(cached);
          age = now - entry.timestamp;
        }
      } catch (error) {
        // Ignore parsing errors
      }

      return { key, size, age };
    });

    const totalSize = entries.reduce((sum, entry) => sum + entry.size, 0);

    return {
      totalEntries: entries.length,
      totalSize,
      entries
    };
  }
}
